import get from 'lodash/get';
import find from 'lodash/find';
import remove from 'lodash/remove';
import partition from 'lodash/partition';
import cloneDeep from 'lodash/cloneDeep';

import {
  CREATE_LISTING_PENDING,
  CREATE_LISTING_REJECTED,
  CREATE_LISTING_FULFILLED,
  CREATE_LISTING_NEW_FULFILLED,
  CREATE_LISTING_NEW_PENDING,
  CREATE_LISTING_NEW_REJECTED,
  UPDATE_PRICING_FULFILLED,
  FETCH_PAYMENT_OPTIONS_PENDING,
  FETCH_PAYMENT_OPTIONS_REJECTED,
  FETCH_PAYMENT_OPTIONS_FULFILLED,
  SET_PAYOUT_VIEW,
  SET_REFUND_VIEW,
  PAYOUT_SELECT_VIEW,
  PAYOUT_CARD_FORM,
  PAYOUT_ACH_CHECKING_FORM,
  PAYOUT_ACH_SAVINGS_FORM,
  PAYOUT_EFT_CHECKING_FORM,
  PAYOUT_EFT_SAVINGS_FORM,
  REFUND_CARD_FORM,
  REFUND_SELECT_VIEW,
  SELECT_PAYOUT_METHOD,
  SELECT_REFUND_METHOD,
  CANCEL,
  DONE,
  PAYMENT_REMOVE_PAYMENT_OPTION,
  PAYOUT_UPDATE_PAYMENT_OPTION,
  REFUND_UPDATE_PAYMENT_OPTION,
  PAYMENT_MOVE_OPTION,
  PAYOUT_TYPE_TO_FORM_MAP,
  ADD_EVENT_DATE_TO_FORM,
} from '../constants/index';

import {
  SET_FORM_VALUE,
  POPULATE_FORM,
  CLEAR_FORM,
  SET_FORM_ERROR,
  SET_FORM_ERRORS,
  VALIDATE_FORM_FIELD,
} from 'webapp-common-ui/src/actions/action-types';

import getCardTypeFromBrand from './utils/getCardTypeFromBrand';

import Forms from './forms';

const defaultState = {
  payoutView: PAYOUT_SELECT_VIEW,
  refundView: REFUND_SELECT_VIEW,
  forms: {
    [PAYOUT_CARD_FORM]: Forms.PayoutForm.default(),
    [PAYOUT_ACH_CHECKING_FORM]: Forms.CheckingForm.default(),
    [PAYOUT_ACH_SAVINGS_FORM]: Forms.CheckingForm.default({ accountType: 'savings' }),
    [PAYOUT_EFT_CHECKING_FORM]: Forms.EftForm.default(),
    [PAYOUT_EFT_SAVINGS_FORM]: Forms.EftForm.default({ accountType: 'savings' }),
    [REFUND_CARD_FORM]: Forms.CreditForm.default(),
  },
  fetched: false,
  fetching: false,
  error: null,
  selectedPayout: '',
  selectedRefund: '',
  payoutOptions: [],
  refundOptions: [],
  payoutTypes: [],
  _links: {},
  creating: false,
  created: false,
  createError: null,
  listing: null,
};

export default function pricingReduce(state = defaultState, action) {
  switch (action.type) {
    case PAYMENT_MOVE_OPTION: {
      const listKey = action.payload.where;
      const fromIndex = action.payload.fromIndex;
      const toIndex = action.payload.toIndex;
      const list = [ ...state[listKey] ];

      if (toIndex < 0) {
        return state;
      }

      if (fromIndex > list.length - 1) {
        return state;
      }

      const item = list.splice(fromIndex, 1)[0];
      list.splice(toIndex, 0, item);

      return {
        ...state,
        [listKey]: list,
      };
    }

    case DONE:
    case CANCEL:
      return {
        ...defaultState,
      };

    case PAYOUT_UPDATE_PAYMENT_OPTION: {
      const oldToken = action.payload.token;
      const data = action.payload.data;

      const payoutOptions = state.payoutOptions.map((option) => {
        if (option.token === oldToken) {
          return {
            ...data,
          };
        }

        return option;
      });

      return {
        ...state,
        payoutOptions,
        selectedPayout: data.token,
      };
    }

    case REFUND_UPDATE_PAYMENT_OPTION: {
      const oldToken = action.payload.token;
      const data = action.payload.data;

      const refundOptions = state.refundOptions.map((option) => {
        if (option.token === oldToken) {
          return {
            ...data,
          };
        }

        return option;
      });

      return {
        ...state,
        refundOptions,
        selectedRefund: data.token,
      };
    }

    case PAYMENT_REMOVE_PAYMENT_OPTION: {
      const removedToken = action.payload;

      const payoutView = state.payoutView;
      const payoutOptions = state.payoutOptions;
      const payoutOption = find(payoutOptions, { token: removedToken });

      if (payoutOption) {
        let nextPayoutView = PAYOUT_SELECT_VIEW;
        let selectedPayout = state.selectedPayout;
        remove(payoutOptions, { token: removedToken });

        if (payoutOptions.length === 0) {
          selectedPayout = defaultState.selectedPayout;
          nextPayoutView = PAYOUT_TYPE_TO_FORM_MAP[state.payoutTypes[0]];
        } else if (selectedPayout === removedToken || !selectedPayout) {
          selectedPayout = get(payoutOptions, '[0].token');
        }

        return {
          ...state,
          selectedPayout,
          payoutView: nextPayoutView,
          payoutOptions: [
            ...payoutOptions,
          ],
          forms: {
            ...state.forms,
            [payoutView]: cloneDeep(defaultState.forms[payoutView]),
          },
        };
      }

      const refundView = state.refundView;
      const refundOptions = state.refundOptions;
      const refundOption = find(refundOptions, { token: removedToken });

      if (refundOption) {
        let nextRefundView = REFUND_SELECT_VIEW;
        let selectedRefund = state.selectedRefund;
        remove(refundOptions, { token: removedToken });

        if (refundOptions.length === 0) {
          selectedRefund = defaultState.selectedRefund;
          nextRefundView = REFUND_CARD_FORM;
        } else if (selectedRefund === removedToken || !selectedRefund) {
          selectedRefund = get(refundOptions, '[0].token');
        }

        return {
          ...state,
          selectedRefund,
          refundView: nextRefundView,
          refundOptions: [
            ...refundOptions,
          ],
          forms: {
            ...state.forms,
            [refundView]: cloneDeep(defaultState.forms[refundView]),
          },
        };
      }

      return state;
    }

    case CREATE_LISTING_NEW_PENDING:
    case CREATE_LISTING_PENDING:
      return {
        ...state,
        creating: true,
        created: false,
        createError: null,
        listing: null,
      };

    case CREATE_LISTING_NEW_FULFILLED:
    case UPDATE_PRICING_FULFILLED:
    case CREATE_LISTING_FULFILLED:
      return {
        ...state,
        creating: false,
        created: true,
        createError: null,
        listing: action.payload.body,
      };

    case CREATE_LISTING_NEW_REJECTED: 
    case CREATE_LISTING_REJECTED:
      return {
        ...state,
        creating: false,
        created: false,
        createError: action.payload,
        listing: null,
      };

    case SELECT_PAYOUT_METHOD:
      return {
        ...state,
        payoutOptions: swapSelected(action.payload, state.payoutOptions),
        selectedPayout: action.payload,
      };

    case SELECT_REFUND_METHOD:
      return {
        ...state,
        refundOptions: swapSelected(action.payload, state.refundOptions),
        selectedRefund: action.payload,
      };

    case SET_PAYOUT_VIEW:
      if (state.payoutView === action.payload) {
        return state;
      }

      if (action.payload === PAYOUT_SELECT_VIEW
        && state.selectedPayout === defaultState.selectedPayout) {
        return {
          ...state,
          selectedPayout: get(state.payoutOptions, '[0].token'),
          payoutView: PAYOUT_SELECT_VIEW,
        };
      }

      return {
        ...state,
        selectedPayout: defaultState.selectedPayout,
        payoutView: action.payload,
      };

    case SET_REFUND_VIEW:
      if (state.refundView === action.payload) {
        return state;
      }

      if (action.payload === REFUND_SELECT_VIEW
        && state.selectedRefund === defaultState.selectedRefund) {
        return {
          ...state,
          selectedRefund: get(state.refundOptions, '[0].token'),
          refundView: REFUND_SELECT_VIEW,
        };
      }

      return {
        ...state,
        selectedRefund: defaultState.selectedRefund,
        refundView: action.payload,
      };

    case FETCH_PAYMENT_OPTIONS_PENDING:
      return {
        ...state,
        _links: null,
        selectedPayout: '',
        selectedRefund: '',
        payoutOptions: [],
        refundOptions: [],
        fetched: false,
        fetching: true,
        error: null,
      };

    case FETCH_PAYMENT_OPTIONS_REJECTED:
      return {
        ...state,
        _links: null,
        selectedPayout: '',
        selectedRefund: '',
        payoutOptions: [],
        refundOptions: [],
        fetched: false,
        fetching: false,
        error: action.payload.body,
      };

    case FETCH_PAYMENT_OPTIONS_FULFILLED: {
      const payload = get(action, 'payload.body', {});
      const form = Forms.getForm(REFUND_CARD_FORM);

      form.setDefault({
        refundCardBrands: get(payload, 'clawbackCardBrands', []).map(getCardTypeFromBrand),
      });

      defaultState.forms = {
        ...defaultState.forms,
        [REFUND_CARD_FORM]: form.default(),
      };

      const payoutTypes = get(payload, 'payoutTypes', []);

      // place DEBIT to the first place
      if (payoutTypes.indexOf('DEBIT') > 0) {
        remove(payoutTypes, t => t === 'DEBIT');
        payoutTypes.unshift('DEBIT');
      }

      return {
        ...state,
        _links: get(payload, '_links', {}),
        fetched: true,
        fetching: false,
        error: null,
        selectedPayout: get(payload, 'payoutOption.token', get(payload, 'payoutCandidates[0].token', '')),
        selectedRefund: get(payload, 'clawbackOption.token', get(payload, 'clawbackCandidates[0].token', '')),
        payoutOptions: [ get(payload, 'payoutOption'), ...get(payload, 'payoutCandidates', []) ].filter(v => v),
        refundOptions: [ get(payload, 'clawbackOption'), ...get(payload, 'clawbackCandidates', []) ].filter(v => v),
        payoutTypes,
        forms: {
          ...state.forms,
          [REFUND_CARD_FORM]: {
            ...state.forms[REFUND_CARD_FORM],
            refundCardBrands: get(payload, 'clawbackCardBrands', []).map(getCardTypeFromBrand),
          },
        },
      };
    }

    case ADD_EVENT_DATE_TO_FORM: {
      const form = Forms.getForm(REFUND_CARD_FORM);

      form.setDefault({
        eventDate: get(action, 'payload', []),
      });

      defaultState.forms = {
        ...defaultState.forms,
        [REFUND_CARD_FORM]: form.default(),
      };

      return {
        ...state,
        forms: {
          ...state.forms,
          [REFUND_CARD_FORM]: {
            ...state.forms[REFUND_CARD_FORM],
            eventDate: get(action, 'payload'),
          },
        },
      };
    }

    case SET_FORM_VALUE:
    case POPULATE_FORM:
    case CLEAR_FORM:
    case SET_FORM_ERROR:
    case SET_FORM_ERRORS:
    case VALIDATE_FORM_FIELD:
      return Forms.handleActions(state, action);

    default:
      return state;
  }
}

function swapSelected(key, options) {
  if (!options || !key) {
    return [];
  }

  const patitioned = partition(options, option => option.token === key);

  return [ ...patitioned[0], ...patitioned[1] ];
}
